import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import './HowToPlayPreview.css';
import HandIcon from '../../shared/img/cursor-hand.png';
import HandGrabIcon from '../../shared/img/cursor-hand-grab.png';
import PlayerAvatar, { getPlayerColor } from '../PlayerAvatar/PlayerAvatar';

// ── Colours ───────────────────────────────────────────────────────────────────
const BAR_BG       = ['#fff355', '#608ad6', '#e75151', '#92dc67', '#e17d47'];
const BAR_FG       = ['#333333', '#ffffff', '#ffffff', '#ffffff', '#ffffff'];
const RANK_COLORS  = ['#FFD700', '#B0B0B0', '#CD7F32', '#555555', '#c0392b'];
const RANK_STROKES = ['#8a7100', '#505050', '#7a4c1e', '#222222', '#7a1010'];

const CUES = ['Pizza', 'Sushi', 'Tacos', 'Salad', 'Mochi'];
const FRIEND_NAMES = ['Alex', 'Sam', 'Jordan', 'Taylor', 'Morgan', 'Jamie', 'Riley', 'Casey'];

/** The two lobby families, named and coloured as they are in the lobby's entry picker. */
type Mode = 'coop' | 'versus';
const ACCENT: Record<Mode, string> = { coop: '#D36667', versus: '#7F97C2' };

// ── Order arrays (value = cueIdx at rank position) ────────────────────────────
const INIT_ORDER  : number[] = [3, 1, 4, 0, 2];
const CZAR_ORDER  : number[] = [2, 0, 1, 4, 3]; // Tacos>Pizza>Sushi>Mochi>Salad
// P1 pairwise swaps: (rank0↔4) → (rank1↔3) → (rank2↔3)
const P1_MID1     : number[] = [2, 1, 4, 0, 3]; // swap rank0&4: Tacos↔Salad
const P1_MID2     : number[] = [2, 0, 4, 1, 3]; // swap rank1&3: Pizza↔Sushi
// CZAR_ORDER is after swap rank2&3: Sushi↔Mochi

const YOUR_ORDER  : number[] = [1, 4, 2, 0, 3]; // Sushi>Mochi>Tacos>Pizza>Salad  (score 1200)
// P2 pairwise swaps: (rank0↔1) → (rank1↔4) → (rank1↔2)
const P2_MID1     : number[] = [1, 3, 4, 0, 2]; // swap rank0&1: Sushi↔Salad
const P2_MID2     : number[] = [1, 2, 4, 0, 3]; // swap rank1&4: Tacos↔Salad
// YOUR_ORDER is after swap rank1&2: Mochi↔Tacos

// ── Versus orders ─────────────────────────────────────────────────────────────
// Nobody is predicted in versus: you answer for yourself and are scored against what the group
// as a whole decided. Two adjacent swaps away from the group = 200 lost = 1,800.
const VS_YOUR_ORDER  : number[] = [1, 2, 4, 3, 0]; // Sushi>Tacos>Mochi>Salad>Pizza
const VS_GROUP_ORDER : number[] = [1, 4, 2, 0, 3]; // Sushi>Mochi>Tacos>Pizza>Salad
const VS_MID1        : number[] = [1, 3, 4, 0, 2]; // swap rank0&1: Sushi↔Salad
const VS_MID2        : number[] = [1, 2, 4, 0, 3]; // swap rank1&4: Tacos↔Salad
// VS_YOUR_ORDER is after swap rank3&4: Salad↔Pizza
const VS_SCORE = 1800;

// ── Layout constants (full-size sequence bars) ────────────────────────────────
const BAR_H  = 28;
const BAR_GAP= 5;
const STRIDE = BAR_H + BAR_GAP;        // 33
const SEQ_H  = 5 * STRIDE - BAR_GAP;  // 160

// WRAP_H = sequence + gap + button — used for cursor Y% in panels 1&2
const WRAP_H  = SEQ_H + 10 + 32; // 202
const RY      = (r: number) => ((r * STRIDE + BAR_H / 2) / WRAP_H) * 100;
const SUBMIT_Y = ((SEQ_H + 10 + 16) / WRAP_H) * 100;
const CX = 50;

// ── Timing (seconds → ms, ~2.5× slower than initial draft) ───────────────────
const STEP_MS    = 2400;  // interval between drag steps
const LIFT_DELAY = 1100;  // within a step: delay before bars sort + cursor flies to toRank

// ── Drag step data ────────────────────────────────────────────────────────────
interface DragStep { fromRank: number; toRank: number; orderAfter: number[] }
const P1_STEPS: DragStep[] = [
  { fromRank: 4, toRank: 0, orderAfter: P1_MID1 }, // Tacos from rank4 → rank0 (Salad swaps to rank4)
  { fromRank: 3, toRank: 1, orderAfter: P1_MID2 }, // Pizza from rank3 → rank1 (Sushi swaps to rank3)
  { fromRank: 3, toRank: 2, orderAfter: CZAR_ORDER }, // Sushi from rank3 → rank2 (Mochi swaps to rank3)
];
const P2_STEPS: DragStep[] = [
  { fromRank: 1, toRank: 0, orderAfter: P2_MID1 }, // Sushi from rank1 → rank0 (Salad swaps to rank1)
  { fromRank: 4, toRank: 1, orderAfter: P2_MID2 }, // Tacos from rank4 → rank1 (Salad swaps to rank4)
  { fromRank: 2, toRank: 1, orderAfter: YOUR_ORDER }, // Mochi from rank2 → rank1 (Tacos swaps to rank2)
];
const VS_STEPS: DragStep[] = [
  { fromRank: 1, toRank: 0, orderAfter: VS_MID1 }, // Sushi from rank1 → rank0 (Salad swaps to rank1)
  { fromRank: 4, toRank: 1, orderAfter: VS_MID2 }, // Tacos from rank4 → rank1 (Salad swaps to rank4)
  { fromRank: 4, toRank: 3, orderAfter: VS_YOUR_ORDER }, // Salad from rank4 → rank3 (Pizza swaps to rank4)
];

// ── Versus: the rest of the lobby ─────────────────────────────────────────────
const VS_FRIEND_COUNT = 4;
const VS_LOCK_MS = 850; // gap between friends locking their ranking in

// ── Confetti dots ─────────────────────────────────────────────────────────────
const CONFETTI = [
  { color: '#D36667', tx: '-55px', ty: '-85px', tr: '310deg', delay: '0s'    },
  { color: '#A7C577', tx: '-15px', ty: '-95px', tr: '200deg', delay: '0.08s' },
  { color: '#7F97C2', tx:  '25px', ty: '-90px', tr: '145deg', delay: '0.04s' },
  { color: '#CA8755', tx: '-35px', ty: '-70px', tr: '260deg', delay: '0.14s' },
  { color: '#C6C969', tx:  '40px', ty: '-78px', tr:  '80deg', delay: '0.20s' },
  { color: '#D36667', tx: '-60px', ty: '-60px', tr: '320deg', delay: '0.07s' },
  { color: '#7F97C2', tx:  '55px', ty: '-65px', tr: '170deg', delay: '0.17s' },
  { color: '#A7C577', tx:   '5px', ty:'-100px', tr: '230deg', delay: '0.11s' },
  { color: '#e75151', tx: '-20px', ty: '-88px', tr: '190deg', delay: '0.22s' },
  { color: '#92dc67', tx:  '18px', ty: '-75px', tr: '110deg', delay: '0.03s' },
];

// ── Sub-components ────────────────────────────────────────────────────────────

function Cursor({ x, y, visible, grabbing }: { x: number; y: number; visible: boolean; grabbing: boolean }) {
  return (
    <div className={`htp-cursor${visible ? ' visible' : ''}`} style={{ left: `${x}%`, top: `${y}%` }}>
      <img src={grabbing ? HandGrabIcon : HandIcon} width={22} height={22} alt="" />
    </div>
  );
}

// Renders 5 colour bars. scale < 1 shrinks everything proportionally.
function MiniSequence({ order, liftRank, scale = 1 }: { order: number[]; liftRank: number; scale?: number }) {
  const bh     = Math.round(BAR_H   * scale);
  const gap    = Math.round(BAR_GAP * scale);
  const stride = bh + gap;
  const totalH = 5 * stride - gap;
  const transitionDur = scale === 1 ? '0.85s' : '0s'; // no animation needed on static small seqs

  return (
    <div style={{ position: 'relative', height: totalH, width: '100%' }}>
      {[0, 1, 2, 3, 4].map((cueIdx) => {
        const rank   = order.indexOf(cueIdx);
        const lifted = rank === liftRank;
        return (
          <div key={cueIdx} style={{
            position: 'absolute', left: 0, right: 0,
            top: rank * stride, height: bh,
            transition: `top ${transitionDur} cubic-bezier(0.34, 1.4, 0.64, 1), box-shadow 0.3s, transform 0.3s`,
            display: 'flex', alignItems: 'center', gap: Math.round(6 * scale),
            paddingLeft: Math.round(8 * scale), paddingRight: Math.round(8 * scale),
            borderRadius: Math.round(6 * scale),
            backgroundColor: BAR_BG[cueIdx],
            boxShadow: lifted ? '0 6px 20px rgba(0,0,0,0.28)' : '0 1px 3px rgba(0,0,0,0.08)',
            transform: lifted ? 'scale(1.04)' : 'scale(1)',
            zIndex: lifted ? 2 : 1,
          }}>
            <span style={{
              fontFamily: '"Bangers", cursive',
              fontSize: `${0.95 * scale}rem`,
              color: RANK_COLORS[rank],
              WebkitTextStroke: `${scale}px ${RANK_STROKES[rank]}`,
              lineHeight: 1, minWidth: Math.round(14 * scale), textAlign: 'center',
            }}>{rank + 1}</span>
            <span style={{
              fontSize: `${0.68 * scale}rem`, fontWeight: 600,
              color: BAR_FG[cueIdx], lineHeight: 1,
              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            }}>{CUES[cueIdx]}</span>
          </div>
        );
      })}
    </div>
  );
}

// SVG bezier connector between two sequences showing which cues moved
function SequenceConnector({ leftOrder, rightOrder, scale }: { leftOrder: number[]; rightOrder: number[]; scale: number }) {
  const bh     = Math.round(BAR_H   * scale);
  const gap    = Math.round(BAR_GAP * scale);
  const stride = bh + gap;
  const seqH   = 5 * stride - gap;

  const ly = (c: number) => ((leftOrder.indexOf(c)  * stride + bh / 2) / seqH) * 100;
  const ry = (c: number) => ((rightOrder.indexOf(c) * stride + bh / 2) / seqH) * 100;

  return (
    <svg width="100%" height="100%" viewBox="0 0 100 100" preserveAspectRatio="none"
      style={{ position: 'absolute', inset: 0, display: 'block' }}>
      {[0, 1, 2, 3, 4].map((cueIdx) => {
        const l = ly(cueIdx);
        const r = ry(cueIdx);
        const matches = leftOrder.indexOf(cueIdx) === rightOrder.indexOf(cueIdx);
        return (
          <path key={cueIdx}
            d={`M 0 ${l} C 50 ${l} 50 ${r} 100 ${r}`}
            fill="none"
            stroke={matches ? '#A7C577' : BAR_BG[cueIdx]}
            strokeWidth={matches ? 2.5 : 1.8}
            strokeOpacity={matches ? 0.95 : 0.5}
          />
        );
      })}
    </svg>
  );
}

function ConfettiLayer({ burst }: { burst: boolean }) {
  return (
    <>
      {CONFETTI.map((d, i) => (
        <div key={i} className={`htp-confetti-dot${burst ? ' burst' : ''}`}
          style={{ backgroundColor: d.color,
            '--tx': d.tx, '--ty': d.ty, '--tr': d.tr, '--burst-delay': d.delay,
          } as React.CSSProperties} />
      ))}
    </>
  );
}

/**
 * Versus step 2. There is deliberately no sequence here: the point of versus is that everyone
 * answers for themselves at the same time and you don't get to see it. Locked-in avatars are the
 * whole story — the rankings stay hidden until the group result.
 */
function FriendsLockingIn({ names, lockedCount }: { names: string[]; lockedCount: number }) {
  return (
    <div>
      <div className="htp-friends">
        {names.map((name, i) => {
          const locked = i < lockedCount;
          return (
            <div key={name} className={`htp-friend${locked ? ' locked' : ''}`}>
              <div className="htp-friend-av">
                <PlayerAvatar color={getPlayerColor(i + 1)} size={34} />
                <span className="htp-friend-check">✓</span>
              </div>
              <span className="htp-friend-name">{name}</span>
              <span className="htp-friend-dots">
                <i /><i /><i />
              </span>
            </div>
          );
        })}
      </div>
      <div className="htp-friend-count">
        {lockedCount} / {names.length} locked in
      </div>
    </div>
  );
}

/** Tight stack of the lobby's avatars — labels the group column as "all of them, together". */
function AvatarStack({ count, size = 16 }: { count: number; size?: number }) {
  return (
    <div className="htp-av-stack">
      {Array.from({ length: count }, (_, i) => (
        <span key={i} style={{ marginLeft: i === 0 ? 0 : -size * 0.35, zIndex: count - i }}>
          <PlayerAvatar color={getPlayerColor(i)} size={size} />
        </span>
      ))}
    </div>
  );
}

function ModeToggle({ mode, onChange }: { mode: Mode; onChange: (m: Mode) => void }) {
  return (
    <div className="htp-mode-toggle" role="tablist" aria-label="Game mode">
      {(['coop', 'versus'] as Mode[]).map((m) => (
        <button
          key={m}
          type="button"
          role="tab"
          aria-selected={mode === m}
          className={`htp-mode-btn${mode === m ? ' active' : ''}`}
          onClick={() => onChange(m)}
        >
          {m === 'coop' ? 'Co-op' : 'Versus'}
        </button>
      ))}
    </div>
  );
}

function Panel({ stepNum, title, active, onClick, children }: {
  stepNum: number; title: string; active: boolean; onClick: () => void; children: React.ReactNode;
}) {
  return (
    <div className={`htp-panel${active ? ' active' : ''}`} onClick={onClick} style={{ cursor: 'pointer' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <div className="htp-step-badge">
          <span style={{ fontSize: '0.68rem', fontWeight: 900, color: '#fff', lineHeight: 1 }}>{stepNum}</span>
        </div>
        <span style={{ fontSize: '0.9rem', fontWeight: 700 }}>{title}</span>
      </div>
      {children}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function HowToPlayPreview() {
  const [activePanel, setActivePanel] = useState(1);

  const [p1Order, setP1Order] = useState<number[]>(INIT_ORDER);
  const [p1Lift,  setP1Lift]  = useState(-1);
  const [p1CurX,  setP1CurX]  = useState(CX);
  const [p1CurY,  setP1CurY]  = useState(RY(4));
  const [p1Vis,   setP1Vis]   = useState(false);
  const [p1Grab,  setP1Grab]  = useState(false);
  const [p1Flash, setP1Flash] = useState(false);

  const [p2Order, setP2Order] = useState<number[]>(INIT_ORDER);
  const [p2Lift,  setP2Lift]  = useState(-1);
  const [p2CurX,  setP2CurX]  = useState(CX);
  const [p2CurY,  setP2CurY]  = useState(RY(1));
  const [p2Vis,   setP2Vis]   = useState(false);
  const [p2Grab,  setP2Grab]  = useState(false);
  const [p2Flash, setP2Flash] = useState(false);

  const [score,    setScore]    = useState(0);
  const [confetti, setConfetti] = useState(false);
  const [nameIdx] = useState(() => Math.floor(Math.random() * FRIEND_NAMES.length));

  // Versus
  const [mode, setMode] = useState<Mode>('coop');
  const [lockedCount, setLockedCount] = useState(0);
  const [vsNames] = useState(() => {
    const pool = [...FRIEND_NAMES].sort(() => Math.random() - 0.5);
    return pool.slice(0, VS_FRIEND_COUNT);
  });

  const timers = useRef<ReturnType<typeof setTimeout>[]>([]);
  const sched  = (fn: () => void, ms: number) => { timers.current.push(setTimeout(fn, ms)); };

  // One drag panel's worth of choreography. Co-op runs it twice (the friend, then you); versus
  // runs it once (only you rank — nobody is predicting anybody).
  interface DragApi {
    setOrder: (o: number[]) => void;
    setLift: (r: number) => void;
    setCurX: (x: number) => void;
    setCurY: (y: number) => void;
    setVis: (v: boolean) => void;
    setGrab: (g: boolean) => void;
    setFlash: (f: boolean) => void;
  }
  const P1_API: DragApi = { setOrder: setP1Order, setLift: setP1Lift, setCurX: setP1CurX, setCurY: setP1CurY, setVis: setP1Vis, setGrab: setP1Grab, setFlash: setP1Flash };
  const P2_API: DragApi = { setOrder: setP2Order, setLift: setP2Lift, setCurX: setP2CurX, setCurY: setP2CurY, setVis: setP2Vis, setGrab: setP2Grab, setFlash: setP2Flash };

  function scheduleDrag(t: number, steps: DragStep[], api: DragApi) {
    steps.forEach(({ fromRank, toRank, orderAfter }, i) => {
      const base = t + i * STEP_MS;
      sched(() => { api.setVis(true); api.setGrab(false); api.setCurX(CX); api.setCurY(RY(fromRank)); api.setLift(-1); }, base);
      sched(() => { api.setGrab(true); api.setLift(fromRank); }, base + 600);
      sched(() => { api.setCurY(RY(toRank)); }, base + LIFT_DELAY);
      sched(() => { api.setOrder(orderAfter); api.setLift(-1); }, base + LIFT_DELAY + 200);
      sched(() => { api.setGrab(false); }, base + LIFT_DELAY + 600);
    });
    const end = t + steps.length * STEP_MS;
    sched(() => { api.setCurX(CX + 3); api.setCurY(SUBMIT_Y); }, end);
    sched(() => { api.setFlash(true); }, end + 1200);
    sched(() => { api.setFlash(false); api.setVis(false); }, end + 2000);
    return end;
  }

  const rafRef = useRef<number | null>(null);

  function scheduleScore(t: number, target: number) {
    sched(() => {
      const duration = 1200;
      const start = performance.now();
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      const tick = (now: number) => {
        const p = Math.min((now - start) / duration, 1);
        const eased = 1 - Math.pow(1 - p, 3); // ease-out cubic
        setScore(Math.round(eased * target));
        if (p < 1) rafRef.current = requestAnimationFrame(tick);
      };
      rafRef.current = requestAnimationFrame(tick);
    }, t);
    sched(() => setConfetti(true),  t + 1600);
    sched(() => setConfetti(false), t + 3000);
    return t + 4600;
  }

  // Friends locking in, one after another.
  function scheduleLockIns(t: number) {
    for (let i = 1; i <= VS_FRIEND_COUNT; i++) {
      sched(() => setLockedCount(i), t + i * VS_LOCK_MS);
    }
    return t + VS_FRIEND_COUNT * VS_LOCK_MS;
  }

  function resetAll() {
    timers.current.forEach(clearTimeout);
    timers.current = [];
    setP1Lift(-1); setP1Vis(false); setP1Grab(false); setP1Flash(false);
    setP1CurX(CX); setP1CurY(RY(4));
    setP2Lift(-1); setP2Vis(false); setP2Grab(false); setP2Flash(false);
    setP2CurX(CX); setP2CurY(RY(1));
    setScore(0); setConfetti(false); setLockedCount(0);
  }

  function runCoopLoop(startPanel = 1) {
    resetAll();
    // earlier panels snap to their end state
    setP1Order(startPanel > 1 ? CZAR_ORDER : INIT_ORDER);
    setP2Order(startPanel > 2 ? YOUR_ORDER : INIT_ORDER);

    let t = 600;

    if (startPanel <= 1) {
      setActivePanel(1);
      t = scheduleDrag(t, P1_STEPS, P1_API) + 3000;
      sched(() => setActivePanel(2), t - 200);
    }

    if (startPanel <= 2) {
      if (startPanel === 2) setActivePanel(2);
      t = scheduleDrag(t, P2_STEPS, P2_API) + 3000;
      sched(() => setActivePanel(3), t - 200);
    }

    if (startPanel === 3) setActivePanel(3);
    const loopEnd = scheduleScore(t, 1200);
    sched(() => runCoopLoop(), loopEnd);
  }

  function runVersusLoop(startPanel = 1) {
    resetAll();
    setP1Order(startPanel > 1 ? VS_YOUR_ORDER : INIT_ORDER);
    if (startPanel > 2) setLockedCount(VS_FRIEND_COUNT);

    let t = 600;

    if (startPanel <= 1) {
      setActivePanel(1);
      t = scheduleDrag(t, VS_STEPS, P1_API) + 2600;
      sched(() => setActivePanel(2), t - 200);
    }

    if (startPanel <= 2) {
      if (startPanel === 2) setActivePanel(2);
      t = scheduleLockIns(t) + 2200;
      sched(() => setActivePanel(3), t - 200);
    }

    if (startPanel === 3) setActivePanel(3);
    const loopEnd = scheduleScore(t, VS_SCORE);
    sched(() => runVersusLoop(), loopEnd);
  }

  const runLoop = (startPanel = 1) =>
    mode === 'versus' ? runVersusLoop(startPanel) : runCoopLoop(startPanel);

  useEffect(() => {
    runLoop();
    return () => {
      timers.current.forEach(clearTimeout);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);


  // Panel 3 small sequence scale
  const S3 = 0.68;
  const s3Stride = Math.round((BAR_H + BAR_GAP) * S3);
  const s3SeqH   = 5 * s3Stride - Math.round(BAR_GAP * S3);

  // Both modes end on the same beat: a score out of 2,000.
  const scoreBlock = (
    <div style={{ position: 'relative', textAlign: 'center', marginTop: 10 }}>
      <ConfettiLayer burst={confetti} />
      <div style={{
        fontFamily: '"Bangers", cursive',
        fontSize: score > 0 ? '2.2rem' : '1.6rem',
        color: ACCENT[mode], letterSpacing: 3, lineHeight: 1,
        transition: 'font-size 0.12s', minHeight: '2.4rem',
      }}>
        {score > 0 ? score.toLocaleString() : '—'}
      </div>
      <div style={{ fontSize: '0.58rem', color: 'rgba(0,0,0,0.4)', fontWeight: 600, marginTop: 2 }}>
        pts out of 2,000
      </div>
    </div>
  );

  return (
    <Box sx={{ width: '100%', py: 4, px: 2 }} className={`htp-root htp-root--${mode}`}>
      <Typography component="h2" align="center" sx={{
        fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.12em',
        textTransform: 'uppercase', color: 'rgba(0,0,0,0.35)', mb: 1.5,
      }}>
        How it works
      </Typography>

      <ModeToggle mode={mode} onChange={setMode} />

      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, maxWidth: 860, mx: 'auto' }}>

      {mode === 'coop' ? (
        <>
        {/* ── Panel 1 ── */}
        <Panel stepNum={1} title={`Your friend ${FRIEND_NAMES[nameIdx]} ranks`} active={activePanel === 1} onClick={() => runLoop(1)}>
          <Typography component="p" variant="h1" align="center" sx={{ fontSize: '0.9rem', mb: 1.5 }}>
            Which one is <Typography component="span" variant="h1" color="primary" sx={{ fontSize: 'inherit' }}>your</Typography> favourite food?
          </Typography>
          <div style={{ position: 'relative' }}>
            <MiniSequence order={p1Order} liftRank={p1Lift} />
            <div style={{ marginTop: 10 }}>
              <div className={`htp-submit-btn${p1Flash ? ' flash' : ''}`}>Submit ranking</div>
            </div>
            <Cursor x={p1CurX} y={p1CurY} visible={p1Vis} grabbing={p1Grab} />
          </div>
        </Panel>

        {/* ── Panel 2 ── */}
        <Panel stepNum={2} title="You predict" active={activePanel === 2} onClick={() => runLoop(2)}>
          <Typography component="p" variant="h1" align="center" sx={{ fontSize: '0.9rem', mb: 1.5 }}>
            Which one is <Typography component="span" variant="h1" color="primary" sx={{ fontSize: 'inherit' }}>{FRIEND_NAMES[nameIdx]}</Typography>'s favourite food?
          </Typography>
          <div style={{ position: 'relative' }}>
            <MiniSequence order={p2Order} liftRank={p2Lift} />
            <div style={{ marginTop: 10 }}>
              <div className={`htp-submit-btn${p2Flash ? ' flash' : ''}`}>Submit prediction</div>
            </div>
            <Cursor x={p2CurX} y={p2CurY} visible={p2Vis} grabbing={p2Grab} />
          </div>
        </Panel>

        {/* ── Panel 3 ── */}
        <Panel stepNum={3} title="Score points" active={activePanel === 3} onClick={() => runLoop(3)}>
          <Typography component="p" variant="h1" align="center" sx={{ fontSize: '0.9rem', mb: 1.5 }}>
            The closer <Typography component="span" variant="h1" color="primary" sx={{ fontSize: 'inherit' }}>your</Typography> prediction, the more points
          </Typography>

          {/* Fan-out: two small sequences + bezier connector between them */}
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 0, position: 'relative' }}>

            {/* Alex's sequence — tilted left */}
            <div style={{ width: '40%', flexShrink: 0, position: 'relative', zIndex: 1 }}>
              <p style={{ fontSize: '0.52rem', textAlign: 'center', color: 'rgba(0,0,0,0.4)',
                margin: '0 0 4px', fontWeight: 600, letterSpacing: '0.05em' }}>{FRIEND_NAMES[nameIdx].toUpperCase()}</p>
              <div style={{ transform: 'rotate(-5deg)', transformOrigin: 'top right' }}>
                <MiniSequence order={CZAR_ORDER} liftRank={-1} scale={S3} />
              </div>
            </div>

            {/* SVG connector */}
            <div style={{ flex: 1, position: 'relative', height: s3SeqH + 16, zIndex: 0 }}>
              <div style={{ position: 'absolute', top: 16, left: 0, right: 0, bottom: 0 }}>
                <SequenceConnector leftOrder={CZAR_ORDER} rightOrder={YOUR_ORDER} scale={S3} />
              </div>
            </div>

            {/* Your sequence — tilted right */}
            <div style={{ width: '40%', flexShrink: 0, position: 'relative', zIndex: 1 }}>
              <p style={{ fontSize: '0.52rem', textAlign: 'center', color: 'rgba(0,0,0,0.4)',
                margin: '0 0 4px', fontWeight: 600, letterSpacing: '0.05em' }}>YOU</p>
              <div style={{ transform: 'rotate(5deg)', transformOrigin: 'top left' }}>
                <MiniSequence order={YOUR_ORDER} liftRank={-1} scale={S3} />
              </div>
            </div>

          </div>

          {scoreBlock}
        </Panel>
        </>
      ) : (
        <>
        {/* ── Versus panel 1: you rank ── */}
        <Panel stepNum={1} title="You rank" active={activePanel === 1} onClick={() => runLoop(1)}>
          <Typography component="p" variant="h1" align="center" sx={{ fontSize: '0.9rem', mb: 1.5 }}>
            Which one is <Typography component="span" variant="h1" sx={{ fontSize: 'inherit', color: ACCENT.versus }}>your</Typography> favourite food?
          </Typography>
          <div style={{ position: 'relative' }}>
            <MiniSequence order={p1Order} liftRank={p1Lift} />
            <div style={{ marginTop: 10 }}>
              <div className={`htp-submit-btn${p1Flash ? ' flash' : ''}`}>Submit ranking</div>
            </div>
            <Cursor x={p1CurX} y={p1CurY} visible={p1Vis} grabbing={p1Grab} />
          </div>
        </Panel>

        {/* ── Versus panel 2: everyone else ranks, hidden ── */}
        <Panel stepNum={2} title="Everyone else ranks" active={activePanel === 2} onClick={() => runLoop(2)}>
          <Typography component="p" variant="h1" align="center" sx={{ fontSize: '0.9rem', mb: 1.5 }}>
            Same question, <Typography component="span" variant="h1" sx={{ fontSize: 'inherit', color: ACCENT.versus }}>everyone</Typography> answers for themselves
          </Typography>
          <FriendsLockingIn names={vsNames} lockedCount={lockedCount} />
        </Panel>

        {/* ── Versus panel 3: compare to the group ── */}
        <Panel stepNum={3} title="Compare to the group" active={activePanel === 3} onClick={() => runLoop(3)}>
          <Typography component="p" variant="h1" align="center" sx={{ fontSize: '0.9rem', mb: 1.5 }}>
            The closer to the <Typography component="span" variant="h1" sx={{ fontSize: 'inherit', color: ACCENT.versus }}>group's</Typography> ranking, the more points
          </Typography>

          <div style={{
            display: 'grid',
            gridTemplateColumns: '40% minmax(0, 1fr) 40%',
            gridTemplateRows: 'auto auto',
            alignItems: 'start',
            position: 'relative',
          }}>

            {/* Both labels occupy the same grid row. The taller group label determines the row
                height, so the two sequences and the connector below always share one origin. */}
            <div style={{ gridColumn: 1, gridRow: 1, position: 'relative', zIndex: 1 }}>
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 2 }}>
                <AvatarStack count={VS_FRIEND_COUNT + 1} />
              </div>
              <p style={{ fontSize: '0.52rem', textAlign: 'center', color: 'rgba(0,0,0,0.4)',
                margin: '0 0 4px', fontWeight: 600, letterSpacing: '0.05em' }}>THE GROUP</p>
            </div>

            <div style={{
              gridColumn: 3,
              gridRow: 1,
              alignSelf: 'stretch',
              display: 'flex',
              alignItems: 'flex-end',
              justifyContent: 'center',
              position: 'relative',
              zIndex: 1,
            }}>
              <p style={{ fontSize: '0.52rem', textAlign: 'center', color: 'rgba(0,0,0,0.4)',
                margin: '0 0 4px', fontWeight: 600, letterSpacing: '0.05em' }}>YOU</p>
            </div>

            {/* The group's aggregate ranking — tilted left. */}
            <div style={{ gridColumn: 1, gridRow: 2, position: 'relative', zIndex: 1 }}>
              <div style={{ transform: 'rotate(-5deg)', transformOrigin: 'top right' }}>
                <MiniSequence order={VS_GROUP_ORDER} liftRank={-1} scale={S3} />
              </div>
            </div>

            {/* No guessed header offset: this SVG occupies the exact same grid row as both
                sequences, so its five endpoints meet the five bar centres. */}
            <div style={{ gridColumn: 2, gridRow: 2, position: 'relative', height: s3SeqH, zIndex: 0 }}>
              <SequenceConnector leftOrder={VS_GROUP_ORDER} rightOrder={VS_YOUR_ORDER} scale={S3} />
            </div>

            {/* Your ranking — tilted right */}
            <div style={{ gridColumn: 3, gridRow: 2, position: 'relative', zIndex: 1 }}>
              <div style={{ transform: 'rotate(5deg)', transformOrigin: 'top left' }}>
                <MiniSequence order={VS_YOUR_ORDER} liftRank={-1} scale={S3} />
              </div>
            </div>

          </div>

          {scoreBlock}
        </Panel>
        </>
      )}

      </Box>
    </Box>
  );
}
